import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { errorToast, successToast } from '@/helpers';
import type { components } from '@/generated/internal-api/openapi-types';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { TimedDeleteConfirmation } from '@/components/Modals/ConfirmDelete/TimedDeleteConfirmation';
import { countAffectedStudies, describeApiErrors, validateGridBeforeSave } from '@/utils/treatmentBulkSave.utils';
import { findTreatmentForRow, type TreatmentTypeEditRow } from '@/utils/treatmentTypeValidation';

import { API_ID_PROPERTY } from './BulkEditTreatments.utils';
import BulkEditTreatmentsTable from './BulkEditTreatmentsTable';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1.schema'];
type TreatmentUpdatePayloadItem = components['schemas']['TreatmentTypeGlossaryUpdateItemV1.schema'];

interface HotTableRefLike {
  hotInstance?: {
    getSourceData?: () => Array<TreatmentTypeEditRow>;
    countRows?: () => number;
    validateCells?: (callback?: (valid: boolean) => void) => void;
    validateRows?: (rows: Array<number>, callback?: (valid: boolean) => void) => void;
  } | null;
}

interface BulkEditTreatmentsProps {
  treatments: Array<TreatmentTypeGlossary>;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * Modal for renaming treatment glossary types in bulk. All edited rows are sent in a single
 * atomic update request; archived rows are read-only and omitted from the payload.
 */
const BulkEditTreatments: FC<BulkEditTreatmentsProps> = ({ treatments, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<Array<TreatmentUpdatePayloadItem> | null>(null);
  const hotTableRef = useRef<HotTableRefLike | null>(null);

  const hasEditableRows = useMemo(() => treatments.some(({ archived_at }) => _isNil(archived_at)), [treatments]);

  const initialData: Array<TreatmentTypeEditRow> = useMemo(
    () => treatments.map(({ name, api_id }) => ({ name, api_id })),
    [treatments]
  );

  const handleValidationChange = useCallback((invalid: boolean): void => {
    setHasValidationErrors(invalid);
  }, []);

  const commitUpdate = async (payload: Array<TreatmentUpdatePayloadItem>): Promise<void> => {
    setSaving(true);

    try {
      await ApiService.call({
        endpoint: 'PATCH /api/v1/team-glossary/treatment-types/update',
        path: undefined,
        body: { treatment_types: payload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-treatments-save' } },
      });

      successToast(`Successfully updated ${payload.length} treatment${payload.length === 1 ? '' : 's'}`);
      onSave();
    } catch (error) {
      const detail = describeApiErrors(error);
      errorToast(_isNotBlank(detail) ? `Failed to update treatments: ${detail}` : 'Failed to update treatments');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update treatments', { cause: error, slug: 'bulk-edit-treatments-save' })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    const valid = await validateGridBeforeSave(hotInstance);
    if (!valid) {
      errorToast('Please correct the highlighted treatments before updating');
      return;
    }

    const currentData = hotInstance.getSourceData?.() ?? [];

    // Each row maps back to a treatment by its hidden api_id; a row count that no longer
    // matches means the grid gained or lost rows and no mapping can be trusted.
    if (currentData.length !== treatments.length) {
      errorToast('Unable to update treatments, please reopen this dialog and try again');
      ExceptionHandler.captureException(
        new InVivoError('Bulk edit treatments row count diverged from selection', {
          slug: 'bulk-edit-treatments-row-mismatch',
          context: { rowCount: `${currentData.length}`, treatmentCount: `${treatments.length}` },
        })
      );
      return;
    }

    const payload = currentData.reduce<Array<TreatmentUpdatePayloadItem>>((items, row) => {
      const treatment = findTreatmentForRow(treatments, row[API_ID_PROPERTY]);
      if (_isNil(treatment) || _notNil(treatment.archived_at)) {
        return items;
      }

      const name = (row.name ?? '').toString().trim();
      if (name === treatment.name) {
        return items;
      }

      items.push({ api_id: treatment.api_id, name });
      return items;
    }, []);

    if (payload.length === 0) {
      errorToast(
        hasEditableRows ? 'No treatment names have been changed' : 'There are no editable treatments to update'
      );
      return;
    }

    // A rename rewrites historical data in every study referencing the treatment, so an in-use
    // rename has to be confirmed before it is sent.
    if (countAffectedStudies(treatments, payload) === 0) {
      await commitUpdate(payload);
      return;
    }

    setPendingUpdate(payload);
  };

  const affectedStudyCount = useMemo(
    () => (_isNil(pendingUpdate) ? 0 : countAffectedStudies(treatments, pendingUpdate)),
    [treatments, pendingUpdate]
  );

  if (_notNil(pendingUpdate)) {
    return (
      <TimedDeleteConfirmation
        title="Are you sure you want to update these treatments?"
        render={() => (
          <div className="flex flex-column">
            <div className="mt2">
              This change will update historical data for these treatments and diseases across{' '}
              <span>{affectedStudyCount}</span> studies.
            </div>
            <div className="mt3 fw8">This action can't be undone.</div>
          </div>
        )}
        btnTxt="Update treatments"
        btnColor="dark-blue"
        delayDuration="5s"
        onClick={() => commitUpdate(pendingUpdate)}
        onClickClose={false}
        onCancel={() => setPendingUpdate(null)}
        onCancelClose={false}
        showLoadingOnClick
      />
    );
  }

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader
        title="Edit treatments"
        subText="Editing these treatments will update all past and future dosages that reference them."
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        <BulkEditTreatmentsTable
          data={initialData}
          innerRef={hotTableRef}
          treatments={treatments}
          setInvalid={handleValidationChange}
        />
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitBtnText="Update"
        cancelBtnText="Close"
        submitButtonProps={{
          loading: saving,
          disabled: saving || hasValidationErrors || !hasEditableRows,
          testId: 'bulk-edit-treatments-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          testId: 'bulk-edit-treatments-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkEditTreatments;
